import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Lock as LockIcon,
  AccountCircle as AccountCircleIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { formatApiReachabilityError } from '../utils/browserStorage';

const LoginForm: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotProviderId, setForgotProviderId] = useState<number | ''>('');
  const [forgotProviders, setForgotProviders] = useState<Array<{ id: number; name: string; type?: string | null }>>([]);
  const [forgotStep, setForgotStep] = useState<'email' | 'provider'>('email');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await apiService.login(identifier.trim(), password);
      // Évite d’afficher messages / objets du compte précédent (cache React Query).
      queryClient.clear();
      if (apiService.isCurrentUserSuperAdmin()) {
        navigate('/select-provider');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(
        formatApiReachabilityError(err) ||
          err.response?.data?.error ||
          err.message ||
          'Erreur de connexion'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForgotDialog = () => {
    setForgotEmail('');
    setForgotProviderId('');
    setForgotProviders([]);
    setForgotStep('email');
    setForgotLoading(false);
    setForgotMessage(null);
    setForgotError(null);
  };

  const openForgotDialog = () => {
    setForgotOpen(true);
    setForgotEmail(identifier.includes('@') ? identifier : '');
    setForgotProviderId('');
    setForgotProviders([]);
    setForgotStep('email');
    setForgotLoading(false);
    setForgotMessage(null);
    setForgotError(null);
  };

  const closeForgotDialog = () => {
    setForgotOpen(false);
    resetForgotDialog();
  };

  const handleForgotContinue = async () => {
    if (!forgotEmail.trim()) {
      setForgotError('Veuillez saisir votre adresse email.');
      return;
    }

    setForgotLoading(true);
    setForgotError(null);
    setForgotMessage(null);

    try {
      const optionsResponse = await apiService.getProviderForgotPasswordOptions(forgotEmail.trim());
      const providers = optionsResponse.data.providers || [];

      if (providers.length > 1) {
        setForgotProviders(providers);
        setForgotStep('provider');
        setForgotProviderId('');
        return;
      }

      const singleProviderId = providers.length === 1 ? providers[0].id : undefined;
      const response = await apiService.forgotPassword(forgotEmail.trim(), singleProviderId);
      setForgotMessage(
        response.data?.message || 'Si cet email existe, un lien de reinitialisation a ete envoye.'
      );
    } catch (err: any) {
      setForgotError(err.response?.data?.error || 'Impossible de traiter la demande.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotSendWithProvider = async () => {
    if (!forgotProviderId) {
      setForgotError('Veuillez choisir un partenaire.');
      return;
    }

    setForgotLoading(true);
    setForgotError(null);
    setForgotMessage(null);

    try {
      const response = await apiService.forgotPassword(forgotEmail.trim(), Number(forgotProviderId));
      setForgotMessage(
        response.data?.message || 'Si cet email existe, un lien de reinitialisation a ete envoye.'
      );
    } catch (err: any) {
      setForgotError(err.response?.data?.error || 'Impossible de traiter la demande.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          marginTop: { xs: 3, sm: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/assets/sherlook-logo.svg`}
            alt="Sherlook"
            sx={{
              width: '100%',
              maxWidth: 280,
              height: 'auto',
              mb: 2,
              display: 'block',
            }}
          />

          <Typography component="h2" variant="h5" gutterBottom sx={{ fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>
            Dashboard Partenaire
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Connectez-vous pour gérer vos objets trouvés
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="identifier"
              label="Identifiant ou email"
              name="identifier"
              autoComplete="username"
              autoFocus
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              InputProps={{
                startAdornment: <AccountCircleIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, color: 'action.active' }} />,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      onClick={() => setShowPassword((v) => !v)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disableElevation
              sx={{ mt: 3, mb: 2, py: 1.25, fontWeight: 700 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Se connecter'
              )}
            </Button>
            <Box sx={{ textAlign: 'right' }}>
              <Link component="button" type="button" onClick={openForgotDialog} underline="hover">
                Mot de passe oublie ?
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Dialog open={forgotOpen} onClose={closeForgotDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Mot de passe oublie</DialogTitle>
        <DialogContent>
          {forgotError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {forgotError}
            </Alert>
          )}
          {forgotMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {forgotMessage}
            </Alert>
          )}

          <TextField
            margin="normal"
            fullWidth
            label="Adresse email"
            type="email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            disabled={forgotLoading || !!forgotMessage}
          />

          {forgotStep === 'provider' && !forgotMessage && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Partenaire</InputLabel>
              <Select
                label="Partenaire"
                value={forgotProviderId}
                onChange={(e) => setForgotProviderId(Number(e.target.value))}
                disabled={forgotLoading}
              >
                {forgotProviders.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}{provider.type ? ` (${provider.type})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForgotDialog}>Fermer</Button>
          {!forgotMessage && (
            forgotStep === 'provider' ? (
              <Button variant="contained" onClick={handleForgotSendWithProvider} disabled={forgotLoading}>
                {forgotLoading ? <CircularProgress size={20} color="inherit" /> : 'Envoyer le lien'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleForgotContinue} disabled={forgotLoading}>
                {forgotLoading ? <CircularProgress size={20} color="inherit" /> : 'Continuer'}
              </Button>
            )
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginForm;